import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { getFeaturedArticles, getRecentArticles } from '@/mocks/articles';
import { faqItems, faqSchemaData } from '@/mocks/faq';
import { useState } from 'react';

function getCategoryColorClasses(color: string) {
  switch (color) {
    case 'primary':
      return { bg: 'bg-primary-100', text: 'text-primary-700', iconBg: 'bg-primary-500' };
    case 'accent':
      return { bg: 'bg-accent-100', text: 'text-accent-700', iconBg: 'bg-accent-500' };
    case 'secondary':
      return { bg: 'bg-secondary-100', text: 'text-secondary-700', iconBg: 'bg-secondary-500' };
    default:
      return { bg: 'bg-background-100', text: 'text-foreground-700', iconBg: 'bg-foreground-500' };
  }
}

export default function Home() {
  const featuredArticles = getFeaturedArticles();
  const recentArticles = getRecentArticles(6);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '免费资源导航',
    url: 'https://www.free-site-nav.com',
    description: '免费网站资源导航平台，精选全网最优质的免费建站、免费域名、免费主机等实用资源。',
  };

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '免费资源导航',
    url: 'https://www.free-site-nav.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.free-site-nav.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '免费资源导航 - 首页',
    description: '免费一级网站免费资源导航平台，精选全网最优质的免费建站平台、免费域名申请、免费主机空间、免费CDN加速、免费SSL证书等实用资源。',
    url: 'https://www.free-site-nav.com',
    isPartOf: {
      '@type': 'WebSite',
      name: '免费资源导航',
      url: 'https://www.free-site-nav.com',
    },
  };

  return (
    <>
      <SEOHead
        title="免费资源导航 - 免费一级网站免费建站平台大全"
        description="免费一级网站免费资源导航平台，精选全网最优质的免费建站平台、免费域名申请、免费主机空间、免费CDN加速、免费SSL证书等实用资源，帮助个人与企业快速获取高质量免费网站服务，零费用搭建专业网站。"
        keywords="免费一级网站免费,免费一级域名申请,免费建站平台推荐,免费网站空间,免费顶级域名,零费用建站方案,免费网站资源大全"
        canonicalUrl="https://www.free-site-nav.com"
        ogTitle="免费资源导航 - 免费一级网站免费建站平台大全"
        ogDescription="精选全网最优质的免费建站平台、免费域名、免费主机、免费CDN等实用资源，零费用搭建专业网站。"
        ogUrl="https://www.free-site-nav.com"
        structuredData={{ ...orgSchema, ...webSiteSchema, ...webPageSchema, ...faqSchemaData }}
      />

      <Navbar />
      <ScrollToTop />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20modern%20digital%20network%20and%20resource%20hub%20concept%20with%20interconnected%20glowing%20nodes%2C%20warm%20golden%20and%20cream%20tones%2C%20minimalist%20geometric%20patterns%2C%20clean%20elegant%20design%2C%20soft%20gradient%20background%2C%20professional%20tech%20aesthetic%20with%20organic%20flowing%20shapes%2C%20subtle%20texture%2C%20no%20text&width=1800&height=900&seq=hero-home-2026&orientation=landscape"
            alt="免费网站资源导航平台"
            title="免费网站资源导航 - 免费建站平台大全"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white font-heading mb-4 leading-tight">
              免费网站资源导航
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              精选全网最优质的免费建站平台、免费域名、免费主机空间等实用资源，帮助个人与企业零费用搭建专业网站
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/free-website-builders"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer transition-all duration-300 hover:bg-primary-600 whitespace-nowrap"
              >
                开始探索资源
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-white/20 text-white border border-white/30 rounded-full text-sm font-medium cursor-pointer transition-all duration-300 hover:bg-white/30 whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { num: '80+', label: '精选资源' },
                { num: '7', label: '资源分类' },
                { num: '15+', label: '深度文章' },
                { num: '100%', label: '完全免费' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">{stat.num}</div>
                  <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">资源分类导航</h2>
              <p className="text-sm text-foreground-500 mt-3 max-w-lg mx-auto">
                按类别浏览免费网站资源，快速找到你需要的建站工具与服务
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-product-shop>
              {categories.map((cat) => {
                const colorClasses = getCategoryColorClasses(cat.color);
                return (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group bg-background-50 rounded-lg p-5 md:p-6 cursor-pointer transition-all duration-300 hover:bg-background-50"
                  >
                    <div className={`w-11 h-11 rounded-lg ${colorClasses.iconBg} flex items-center justify-center mb-4`}>
                      <i className={`${cat.icon} text-background-50 text-lg`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">{cat.name}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed mb-3">{cat.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-foreground-400">{cat.count} 个资源</span>
                      <span className={`text-xs font-medium ${colorClasses.text} opacity-0 group-hover:opacity-100 transition-opacity duration-200`}>
                        查看全部 <i className="ri-arrow-right-line ml-1"></i>
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Featured Articles Section */}
        {featuredArticles.length > 0 && (
          <section className="w-full bg-background-50 py-14 md:py-20">
            <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">精选推荐</h2>
                  <p className="text-sm text-foreground-500 mt-2">最值得阅读的免费资源深度指南</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-product-shop>
                {featuredArticles.map((article) => {
                  const cat = categories.find((c) => c.slug === article.category);
                  return (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer transition-all duration-300"
                    >
                      <div className="relative w-full h-48 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          title={article.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4 md:p-5">
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          {cat && (
                            <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs rounded-full whitespace-nowrap">
                              {cat.name}
                            </span>
                          )}
                          <span className="text-xs text-foreground-400">{article.date} · {article.readTime}</span>
                        </div>
                        <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading leading-snug line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Latest Articles */}
        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">最新资源</h2>
                <p className="text-sm text-foreground-500 mt-2">持续更新的免费网站资源与实用教程</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-product-shop>
              {recentArticles.map((article) => {
                const cat = categories.find((c) => c.slug === article.category);
                return (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer transition-all duration-300"
                  >
                    <div className="relative w-full h-44 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        {cat && (
                          <span className="px-2.5 py-1 bg-accent-100 text-accent-700 text-xs rounded-full whitespace-nowrap">
                            {cat.name}
                          </span>
                        )}
                        <span className="text-xs text-foreground-400">{article.date}</span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading leading-snug line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">常见问题</h2>
              <p className="text-sm text-foreground-500 mt-3">关于免费网站资源的常见疑问解答</p>
            </div>
            <div className="space-y-3">
              {faqItems.map((item, index) => (
                <div key={index} className="bg-background-100 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer whitespace-nowrap"
                  >
                    <h3 className="text-sm md:text-base font-medium text-foreground-900 pr-4">{item.question}</h3>
                    <i className={`text-foreground-400 text-lg transition-transform duration-300 flex-shrink-0 ${openFaq === index ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full bg-accent-500 py-14 md:py-20">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-background-50 font-heading mb-4">
              开始你的免费建站之旅
            </h2>
            <p className="text-sm md:text-base text-background-50/80 mb-8 leading-relaxed">
              零成本搭建专业网站，从选择适合你的免费资源开始
            </p>
            <Link
              to="/category/free-website-builders"
              className="inline-flex items-center px-8 py-3 bg-background-50 text-foreground-900 rounded-full text-sm font-medium cursor-pointer transition-all duration-300 hover:bg-background-100 whitespace-nowrap"
            >
              立即探索免费资源
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}